/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

/**
 * Provider / model label helpers for the roadmap-generation UI.
 *
 * The pre-2026-08-26 dropdown hardcoded strings like "Groq Free (LLaMA 8B)"
 * that were ALREADY LYING — the backend had rotated Groq to
 * openai/gpt-oss-20b via env var, but the dropdown and the
 * "Generated by:" display still showed the historical LLaMA name. This
 * module keeps the label source-of-truth in one place: dropdown labels
 * come from the /api/llm/providers registry (what's actually
 * configured); "Generated by" labels come from the value in Mongo,
 * with a historical map so old roadmap docs still render meaningfully.
 */

const PROVIDER_DISPLAY_NAMES = {
  ollama: 'Ollama',
  huggingface: 'HuggingFace',
  groq: 'Groq',
  gemini: 'Google Gemini',
  anthropic: 'Anthropic Claude',
  openai: 'OpenAI'
};

/**
 * Map (provider, tier) → the `llmPreference` string the backend expects.
 * Backend consumer: services/core-api/src/routes/roadmap.js
 * #mapLlmPreferenceToProviderOpts. Keep in lockstep with that switch.
 */
export function providerToLlmPreference(providerName, tier) {
  if (tier === 'free') {
    if (providerName === 'groq') return 'groq-free';
    if (providerName === 'gemini') return 'gemini-free';
  }
  if (providerName === 'anthropic') return 'claude';
  return providerName; // 'groq', 'gemini', 'openai'
}

/**
 * Render a dropdown option label from a live-configured provider + tier.
 * e.g., formatDropdownLabel('groq', 'openai/gpt-oss-120b', 'paid')
 *       → "Groq (openai/gpt-oss-120b)"
 * e.g., formatDropdownLabel('gemini', 'gemini-2.5-flash', 'free')
 *       → "Google Gemini Free (gemini-2.5-flash)"
 */
export function formatDropdownLabel(providerName, model, tier) {
  const display = PROVIDER_DISPLAY_NAMES[providerName] || providerName;
  const tierSuffix = tier === 'free' ? ' Free' : '';
  return `${display}${tierSuffix} (${model})`;
}

// Historical `generatedBy` strings written by the pre-2026-08-26
// per-provider ladder. New writes use `<provider>/<model>` derived
// from what aiService actually called. Historical values stay in
// Mongo forever; this map lets them still render meaningfully.
const HISTORICAL_GENERATED_BY_DISPLAY = {
  'Mistral-7B-HuggingFace': 'HuggingFace (Mistral-7B)',
  'Claude-3.5-Sonnet': 'Anthropic Claude (3.5 Sonnet)',
  'GPT-4': 'OpenAI (GPT-4)',
  'Groq-LLaMA-8B': 'Groq Free (LLaMA-8B)',
  'Groq-LLaMA-70B': 'Groq (LLaMA-70B)',
  'Gemini-Flash': 'Google Gemini Free (Flash)',
  'Gemini-Pro': 'Google Gemini (Pro)',
  'AI': 'AI'
};

/**
 * Render a friendly "Generated by:" label from the raw generatedBy
 * value stored on the Mongo Roadmap doc. Handles both:
 *   - new format:  "<provider>/<model>"     (post-2026-08-26)
 *   - historical:  fixed strings            (pre-2026-08-26)
 * Anything unrecognised (e.g., "SLM-llama3.1:8b" from the pre-refactor
 * SLM fallback path) returns as-is so the UI never blanks out.
 */
export function formatGeneratedBy(value) {
  if (!value) return 'Unknown';
  if (value.includes('/')) {
    // Backend flattens '/' within model IDs to '-' before storing, so
    // there is exactly one '/' in a well-formed new value. Defensive
    // split-and-rejoin keeps us safe against future format drift.
    const idx = value.indexOf('/');
    const provider = value.slice(0, idx);
    const model = value.slice(idx + 1);
    const display = PROVIDER_DISPLAY_NAMES[provider] || provider;
    return `${display} (${model})`;
  }
  return HISTORICAL_GENERATED_BY_DISPLAY[value] || value;
}
